import { runAz } from "./az.js";
import { LocalCliError } from "./errors.js";

const APP_NAME = "m8t-webapp";
// Microsoft's first-party Azure CLI app — pre-authorized so
// `az account get-access-token --scope api://<clientId>/.default` needs no consent.
const AZURE_CLI_FIRST_PARTY_APP_ID = "04b07795-8ddb-461a-bbee-02f9e1bf7b46";
const FOUNDRY_SP_APP_ID = "18a66f5f-dbdf-4c17-9dd7-1634712a9cbe";
const USER_IMPERSONATION_SCOPE_ID = "1a7925b5-f871-417a-9b8b-303f9f29fa10";

export interface AppRegResult { tenantId: string; clientId: string; appObjectId: string | null }

/** JSON `az` helper: parse stdout, or null when empty. */
async function azJson<T>(args: string[]): Promise<T | null> {
  const out = (await runAz(args)).trim();
  if (!out) return null;
  return JSON.parse(out) as T;
}

/**
 * Ensure the app reg has Expose-an-API configured (identifierUris +
 * user_impersonation scope + Azure CLI pre-authorization). Idempotent:
 * skips PATCH when all three pieces are already present; reuses the
 * existing scope ID on re-run; appends Azure CLI to preAuthorizedApps
 * if missing without overwriting other entries.
 *
 * Why two PATCHes: Microsoft Graph validates
 * `preAuthorizedApplications.delegatedPermissionIds` against the
 * EXISTING `api.oauth2PermissionScopes` at PATCH time — a scope being
 * added in the same body is not visible to the validator yet, so a
 * single combined PATCH fails with `InvalidValue: Property
 * api.preAuthorizedApplications.delegatedPermissionIds has a
 * Permission Id that cannot be found in the AppPermissions sets.`
 * We split into two sequential PATCHes: scopes first, then preauth.
 * Skipping either is fine when the corresponding state is already correct.
 */
export async function ensureExposeAnApi(appId: string, appObjectId: string): Promise<void> {
  interface AppApiShape {
    uris: string[];
    scopes: { id: string; value: string; [k: string]: unknown }[];
    preauth: { appId: string; delegatedPermissionIds: string[]; [k: string]: unknown }[];
  }
  const current = await azJson<AppApiShape>([
    "ad", "app", "show", "--id", appId,
    "--query", "{uris: identifierUris, scopes: api.oauth2PermissionScopes, preauth: api.preAuthorizedApplications}",
    "--output", "json",
  ]);

  const targetUri = `api://${appId}`;
  const existingUris: string[] = Array.isArray(current?.uris) ? current.uris : [];
  const existingScopes: { id: string; value: string; [k: string]: unknown }[] = Array.isArray(current?.scopes) ? current.scopes : [];
  const existingPreauth: { appId: string; delegatedPermissionIds: string[]; [k: string]: unknown }[] = Array.isArray(current?.preauth) ? current.preauth : [];

  const uriPresent = existingUris.includes(targetUri);
  const userImpersonationScope = existingScopes.find((s) => s.value === "user_impersonation");
  const azureCliPreauth = existingPreauth.find((p) => p.appId === AZURE_CLI_FIRST_PARTY_APP_ID);

  // Reuse existing scope ID when re-running; only generate a new one
  // when no user_impersonation scope exists. This is the key
  // idempotency guarantee — re-running cannot create duplicate scopes.
  const scopeId = userImpersonationScope?.id ?? globalThis.crypto.randomUUID();

  const preauthPresent = (azureCliPreauth?.delegatedPermissionIds.includes(scopeId)) === true;

  if (uriPresent && userImpersonationScope && preauthPresent) {
    return;
  }

  // PATCH A: identifierUris + oauth2PermissionScopes (the scope must
  // exist on the app reg BEFORE Graph will accept a
  // preAuthorizedApplications entry that references its id). Run when
  // either piece is missing.
  if (!uriPresent || !userImpersonationScope) {
    const newScopes = userImpersonationScope
      ? existingScopes
      : [
          ...existingScopes,
          {
            id: scopeId,
            value: "user_impersonation",
            type: "User",
            adminConsentDescription:
              "Allow the application to access m8t on behalf of the signed-in user.",
            adminConsentDisplayName: "Access m8t",
            userConsentDescription:
              "Allow the application to access m8t on your behalf.",
            userConsentDisplayName: "Access m8t",
            isEnabled: true,
          },
        ];

    const bodyA = {
      identifierUris: uriPresent ? existingUris : [...existingUris, targetUri],
      api: { oauth2PermissionScopes: newScopes },
    };

    await runAz([
      "rest", "--method", "PATCH",
      "--url", `https://graph.microsoft.com/v1.0/applications/${appObjectId}`,
      "--headers", "Content-Type=application/json",
      "--body", JSON.stringify(bodyA),
    ]);
  }

  // PATCH B: preAuthorizedApplications. The scope referenced by
  // delegatedPermissionIds now exists on the app reg (either it was
  // there already or PATCH A just added it). Run when Azure CLI's
  // preauth entry is missing OR doesn't include the user_impersonation
  // scope id.
  if (!preauthPresent) {
    let newPreauth: { appId: string; delegatedPermissionIds: string[] }[];
    if (azureCliPreauth) {
      // Azure CLI is already pre-authorized for at least one scope;
      // ensure user_impersonation is in its delegatedPermissionIds.
      const ids = new Set(azureCliPreauth.delegatedPermissionIds);
      ids.add(scopeId);
      newPreauth = existingPreauth.map((p) =>
        p.appId === AZURE_CLI_FIRST_PARTY_APP_ID
          ? { appId: p.appId, delegatedPermissionIds: Array.from(ids) }
          : { appId: p.appId, delegatedPermissionIds: p.delegatedPermissionIds },
      );
    } else {
      newPreauth = [
        ...existingPreauth.map((p) => ({
          appId: p.appId,
          delegatedPermissionIds: p.delegatedPermissionIds,
        })),
        {
          appId: AZURE_CLI_FIRST_PARTY_APP_ID,
          delegatedPermissionIds: [scopeId],
        },
      ];
    }

    const bodyB = {
      api: { preAuthorizedApplications: newPreauth },
    };

    await runAz([
      "rest", "--method", "PATCH",
      "--url", `https://graph.microsoft.com/v1.0/applications/${appObjectId}`,
      "--headers", "Content-Type=application/json",
      "--body", JSON.stringify(bodyB),
    ]);
  }
}

/**
 * Discover or create the Entra app registration for m8t-webapp.
 *
 * BYO path: if `opts.clientId` is provided, short-circuit immediately —
 * the operator is a directory guest and cannot create app regs.
 *
 * Managed path: port of step1_appReg from deploy/setup.mjs.
 */
export async function ensureAppReg(opts: {
  tenantId: string;
  clientId?: string;
}): Promise<AppRegResult> {
  // BYO short-circuit — MUST make zero runAz calls before this return.
  if (opts.clientId) {
    return { tenantId: opts.tenantId, clientId: opts.clientId, appObjectId: null };
  }

  // Managed path: check if app reg already exists
  const existing = await azJson<{ appId: string }[]>(
    ["ad", "app", "list", "--display-name", APP_NAME, "--output", "json"],
  );

  if (existing && existing.length > 0) {
    const app = existing[0];
    const objId = await azJson<string>(
      ["ad", "app", "show", "--id", app.appId, "--query", "id", "--output", "json"],
    );
    if (!objId) {
      throw new LocalCliError({
        code: "APP_REG_OBJECT_ID_MISSING",
        message: `Could not retrieve object ID for existing app reg ${app.appId}.`,
      });
    }
    await ensureExposeAnApi(app.appId, objId);
    return { tenantId: opts.tenantId, clientId: app.appId, appObjectId: objId };
  }

  // Verify a directory admin role before attempting to create the app reg.
  const me = await azJson<{ id: string }>(
    ["ad", "signed-in-user", "show", "--output", "json"],
  );
  if (!me) {
    throw new LocalCliError({
      code: "APP_REG_NO_SIGNED_IN_USER",
      message: "Could not determine the signed-in user.",
      hint: "Run 'az login' to sign in.",
    });
  }

  // memberOf is blind to a guest's directory roles (returns [] even when the role
  // exists); roleManagement/directory/roleAssignments returns them. A 403 here
  // (an un-privileged guest who can't read directory roles) ⇒ inconclusive (null).
  let roles: string[] | null;
  try {
    roles = (await azJson<string[]>([
      "rest", "--method", "GET",
      "--url",
      `https://graph.microsoft.com/v1.0/roleManagement/directory/roleAssignments?$filter=principalId eq '${me.id}'&$expand=roleDefinition`,
      "--query", "value[].roleDefinition.displayName",
      "--output", "json",
    ])) ?? [];
  } catch {
    roles = null; // read denied (403) — can't determine the user's roles
  }

  const adminRoles = [
    "Global Administrator",
    "Application Administrator",
    "Cloud Application Administrator",
  ];
  const hasAdminRole = roles?.some((r) => adminRoles.includes(r)) ?? false;

  if (!hasAdminRole) {
    if (roles === null) {
      throw new LocalCliError({
        code: "APP_REG_ROLE_UNREADABLE",
        message:
          "Couldn't verify your directory admin role — Microsoft Graph denied the read (common for guest accounts).",
        hint:
          "If you're not a directory admin, re-run with --client-id <appId> (BYO app reg per deploy/operator-app-registration-setup.md). If you ARE an admin, ensure Application Administrator is assigned and your account can read directory roles.",
      });
    }
    throw new LocalCliError({
      code: "APP_REG_NO_ADMIN_ROLE",
      message:
        "You don't hold a directory admin role (Application Administrator / Global Administrator / Cloud Application Administrator).",
      hint:
        "Re-run with --client-id <appId> (BYO app reg per deploy/operator-app-registration-setup.md), or have a tenant admin assign you one.",
    });
  }

  // 5-command create procedure (encoded from operator-app-registration-setup.md)

  // 1. Find Foundry SP object ID
  const foundrySpObjId = (await runAz(
    ["ad", "sp", "show", "--id", FOUNDRY_SP_APP_ID, "--query", "id", "--output", "tsv"],
  )).trim();

  // 2. Create the app reg
  const created = await azJson<{ appId: string; id: string }>([
    "ad", "app", "create",
    "--display-name", APP_NAME,
    "--sign-in-audience", "AzureADMyOrg",
    "--output", "json",
  ]);
  if (!created) {
    throw new LocalCliError({
      code: "APP_REG_CREATE_FAILED",
      message: "App registration create returned no output.",
    });
  }

  // 3. PATCH SPA redirect URIs (localhost:3000 for dev)
  await runAz([
    "rest", "--method", "PATCH",
    "--url", `https://graph.microsoft.com/v1.0/applications/${created.id}`,
    "--headers", "Content-Type=application/json",
    "--body", JSON.stringify({ spa: { redirectUris: ["http://localhost:3000"] } }),
  ]);

  // 4. Add Foundry permission
  await runAz([
    "ad", "app", "permission", "add",
    "--id", created.appId,
    "--api", FOUNDRY_SP_APP_ID,
    "--api-permissions", `${USER_IMPERSONATION_SCOPE_ID}=Scope`,
  ]);

  // 5. Create app SP + grant tenant-wide admin consent (via Graph POST,
  // not the broken CLI command)
  const appSpObjId = (await runAz(
    ["ad", "sp", "create", "--id", created.appId, "--query", "id", "--output", "tsv"],
  )).trim();

  await runAz([
    "rest", "--method", "POST",
    "--url", "https://graph.microsoft.com/v1.0/oauth2PermissionGrants",
    "--headers", "Content-Type=application/json",
    "--body", JSON.stringify({
      clientId: appSpObjId,
      consentType: "AllPrincipals",
      resourceId: foundrySpObjId,
      scope: "user_impersonation",
    }),
  ]);

  await ensureExposeAnApi(created.appId, created.id);
  return { tenantId: opts.tenantId, clientId: created.appId, appObjectId: created.id };
}

/**
 * Make a gateway's own URL a valid sign-in redirect for the app registration that
 * gateway ACTUALLY uses (resolved from its AZURE_CLIENT_ID, not by display name --
 * a name lookup could patch a different app reg than the one the gateway points at).
 *
 * WHY THIS EXISTS SEPARATELY FROM `m8t deploy`'s CALL. `deploy` already calls
 * patchRedirectUris -- but the BOOTSTRAP path can never reach it:
 *
 *   1. The installer runs `m8t deploy --client-id <appId>` (entrypoint.sh requires
 *      APP_REG_CLIENT_ID) because it authenticates as a MANAGED IDENTITY, and an MI
 *      holds no DIRECTORY role -- it cannot touch an app registration at all.
 *   2. `--client-id` takes ensureAppReg's BYO short-circuit, which returns
 *      `appObjectId: null`.
 *   3. deploy.ts's `if (app.appObjectId)` guard then SKIPS patchRedirectUris.
 *
 * And `bootstrap launch` cannot do it either: the gateway's FQDN does not exist
 * until the deploy that happens long after launch has returned. So `bootstrap
 * finish` is the FIRST moment where both facts are true at once -- the FQDN exists,
 * and we are running locally as the human, who has directory rights.
 *
 * Without this, a founder's zero-to-live install produces a gateway that serves
 * HTTP 200 and then fails sign-in with AADSTS50011 (redirect URI mismatch) -- found
 * by a human looking at a live shakedown platform, because every automated check
 * passed. Idempotent (patchRedirectUris no-ops when the URI is already present).
 */
export async function ensureGatewayRedirectUri(gatewayClientId: string, fqdn: string): Promise<void> {
  const appObjectId = await azJson<string>(
    ["ad", "app", "show", "--id", gatewayClientId, "--query", "id", "--output", "json"],
  );
  if (!appObjectId) {
    throw new LocalCliError({
      code: "APP_REG_OBJECT_ID_MISSING",
      message: `Could not resolve the object ID for app registration ${gatewayClientId}.`,
      hint: "You need directory read access to the app registration the gateway signs in against.",
    });
  }
  await patchRedirectUris(appObjectId, fqdn);
}

/**
 * Add the deployed Container App FQDN to the app reg's SPA redirect URIs.
 * Port of step6_patchRedirectUris from deploy/setup.mjs.
 */
/** The app-registration object id to write the gateway's redirect URI to, or
 *  null when we have none and cannot get one.
 *
 *  Supplying `--client-id` used to be read as "this app reg is not ours to
 *  touch", so the deployed gateway's own FQDN was never registered and the
 *  founder met AADSTS50011 the first time they signed in. That inference was
 *  wrong in the case that matters most: the installer passes `--client-id` for
 *  an app reg `m8t bootstrap launch` itself just resolved, so the flag was
 *  reporting "foreign" about our own app. Only the managed path patched, which
 *  is why an existing deployment kept working while every fresh install did
 *  not.
 *
 *  Whether a client id was supplied says nothing about whether we may write to
 *  it. The directory is the only thing that actually knows, so we ask it: read
 *  the object id, and treat a refusal as the answer rather than an error. A
 *  genuinely foreign app reg (or a caller without directory read) yields null
 *  and the caller falls back to telling the founder what to add by hand. */
export async function resolveAppObjectIdForWrite(opts: {
  appObjectId: string | null;
  clientId?: string;
}): Promise<string | null> {
  // Already held by the managed path — never spend a directory read to
  // re-derive something we were just handed.
  if (opts.appObjectId) return opts.appObjectId;
  if (!opts.clientId) return null;
  try {
    return (await azJson<string>(
      ["ad", "app", "show", "--id", opts.clientId, "--query", "id", "--output", "json"],
    )) ?? null;
  } catch {
    // No directory read, or an app reg in a tenant we cannot see. Not an
    // error: the deployment is fine, only the convenience of registering the
    // URI for the founder is unavailable.
    return null;
  }
}

export async function patchRedirectUris(appObjectId: string, fqdn: string): Promise<void> {
  const targetUri = `https://${fqdn}`;
  const existing = (await azJson<string[]>(
    ["ad", "app", "show", "--id", appObjectId, "--query", "spa.redirectUris", "--output", "json"],
  )) ?? [];

  if (existing.includes(targetUri)) {
    return;
  }

  const newList = [...existing, targetUri];
  await runAz([
    "rest", "--method", "PATCH",
    "--url", `https://graph.microsoft.com/v1.0/applications/${appObjectId}`,
    "--headers", "Content-Type=application/json",
    "--body", JSON.stringify({ spa: { redirectUris: newList } }),
  ]);
}
